import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { rejectBody } from '../../utils/apiError';

const CASH_URL = '/api/cash';


// GET /api/cash/accounts → every account this caller may use — their branch's
// plus the shared (company-wide) ones — each with its own derived balance, and
// the per-kind totals. Shape is normalized in the page so it tolerates an
// array, a { data:[...] } envelope, or a { hand, bank } map.
export const fetchCashAccounts = createAsyncThunk(
  'cash/fetchAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${CASH_URL}/accounts`);
      // Backend wraps payloads as { success, data: {...} } — unwrap to the inner data.
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// GET /api/cash/statement?from&to&garage → { kpis, opening, movements }
export const fetchCashStatement = createAsyncThunk(
  'cash/fetchStatement',
  async (params, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${CASH_URL}/statement`, { params });
      // Backend wraps payloads as { success, data: {...} } — unwrap to the inner data.
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// POST /api/cash/accounts { kind, label, garage_id? } — a SECOND drawer or
// bank. garage_id omitted = shared (company-wide); set = that branch's own.
// 409 when the name is already taken (handled by the caller).
export const createCashAccount = createAsyncThunk(
  'cash/createAccount',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${CASH_URL}/accounts`, data);
      // Backend wraps payloads as { success, data: {...} } — unwrap to the inner data.
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// PATCH /api/cash/accounts/:id { label?, is_active?, is_default? } — naming an
// account (the Phase-0 seed left label NULL on every company's default hand and
// bank row), retiring an empty one, or promoting a successor. ONE thunk for the
// one endpoint: a second "setActive" thunk would be the same request with a
// different name, and the 409s it can answer with are the same two.
export const updateCashAccount = createAsyncThunk(
  'cash/updateAccount',
  async ({ id, ...patch }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${CASH_URL}/accounts/${id}`, patch);
      // Backend wraps payloads as { success, data: {...} } — unwrap to the inner data.
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// POST /api/cash/accounts/opening { cash_account_id, opening_balance, opening_date }
// PER ACCOUNT — `kind` named one account only while a company had one of each.
// Locked with 409 after the first non-opening movement IN THAT ACCOUNT, or once
// the company's opening balances are signed off (handled by the caller).
export const setOpeningBalance = createAsyncThunk(
  'cash/setOpening',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${CASH_URL}/accounts/opening`, data);
      // Backend wraps payloads as { success, data: {...} } — unwrap to the inner data.
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// POST /api/cash/transfer. Callers pass { amount, from, to, note }, where from
// and to are either an ACCOUNT ID (branch drawer → main safe, the case branch
// accounts exist for) or the legacy kind 'hand'|'bank'.
//
// The backend contract is from_account_id/to_account_id, with from_kind/to_kind
// still accepted and resolved through the default-of-kind lookup. Translated
// here at the API boundary — sending from/to raw made every transfer 400 with
// "from_kind/to_kind must be one of hand|bank".
const KINDS = ['hand', 'bank'];
export const createCashTransfer = createAsyncThunk(
  'cash/transfer',
  async ({ from, to, ...rest }, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${CASH_URL}/transfer`, {
        ...rest,
        ...(KINDS.includes(from) ? { from_kind: from } : { from_account_id: from }),
        ...(KINDS.includes(to) ? { to_kind: to } : { to_account_id: to }),
      });
      // Backend wraps payloads as { success, data: {...} } — unwrap to the inner data.
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// POST /api/cash/adjustment { account_kind, direction, amount, note }
export const createCashAdjustment = createAsyncThunk(
  'cash/adjustment',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${CASH_URL}/adjustment`, data);
      // Backend wraps payloads as { success, data: {...} } — unwrap to the inner data.
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

const cashSlice = createSlice({
  name: 'cash',
  initialState: {
    accounts: null, // raw /accounts payload (normalized in the page)
    statement: null, // { kpis, opening, movements }
    loadingAccounts: false,
    loadingStatement: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCashAccounts.pending, (state) => {
        state.loadingAccounts = true;
      })
      .addCase(fetchCashAccounts.fulfilled, (state, action) => {
        state.accounts = action.payload;
        state.loadingAccounts = false;
      })
      .addCase(fetchCashAccounts.rejected, (state, action) => {
        state.loadingAccounts = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(fetchCashStatement.pending, (state) => {
        state.loadingStatement = true;
      })
      .addCase(fetchCashStatement.fulfilled, (state, action) => {
        state.statement = action.payload;
        state.loadingStatement = false;
      })
      .addCase(fetchCashStatement.rejected, (state, action) => {
        state.loadingStatement = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      });
  },
});

export default cashSlice.reducer;
